/**
 * The weight and body-fat editors: `POST /me/measurements`, append-only.
 *
 * ── A NEW POINT, NEVER AN EDIT ──────────────────────────────────────────────
 * Saving appends a row (`D68`). The idempotency key is minted once when the
 * sheet opens and reused by every retry of that save (`D154`), so a lost
 * response answers the stored row instead of appending a twin. Undo — offered
 * through `onSaved` — is `DELETE /me/measurements/:id` of the row just
 * written, never a second `POST`.
 */

import { measurementsOptions } from "@repo/client/data/profile";
import { formatDay } from "@repo/client/format";
import { measurementInput as measurementSchema } from "@repo/contract/training";
import {
  "profile-editors.bodyfat_label" as bodyFatLabel,
  "profile-editors.bodyfat_saved" as bodyFatSaved,
  "profile-editors.bodyfat_tip" as bodyFatTip,
  "profile-editors.bodyfat_title" as bodyFatTitle,
  "profile-editors.error_field" as errorField,
  "profile-editors.eyebrow_body_today" as eyebrowBodyToday,
  "profile-editors.register" as registerText,
  "profile-editors.weight_label" as weightLabel,
  "onboarding.weight.question" as weightQuestion,
  "profile-editors.weight_saved" as weightSaved,
  "profile-editors.weight_tip" as weightTip,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { useCallback, useState } from "react";
import { useApi } from "../../../data/api";
import {
  fromUnit,
  loadText,
  maxInUnit,
  toUnit,
  unitSymbol,
} from "../../../lib/weight";
import { uuidv7 } from "../../workout/store/uuid-v7";
import { BigNumberForm } from "./big-number-form";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import {
  MEASUREMENT_KINDS,
  type MeasurementEditor as MeasurementEditorId,
  measurementInput,
} from "./payloads";
import { useEditorForm } from "./use-editor-form";
import {
  useDeleteMeasurementMutation,
  useRecordMeasurementMutation,
} from "./use-editor-mutation";

interface MeasurementSpec {
  readonly fallback: number;
  readonly fractionDigits: number;
  readonly label: () => string;
  readonly max: number;
  readonly min: number;
  readonly saved: (value: number) => string;
  readonly step: number;
  readonly tip: () => string;
  readonly title: () => string;
}

/** A2's ranges and steps (editors.tsx:660-718). */
const SPECS: Readonly<Record<MeasurementEditorId, MeasurementSpec>> = {
  bodyFat: {
    fallback: 20,
    fractionDigits: 1,
    label: bodyFatLabel,
    max: 60,
    min: 3,
    saved: (value) =>
      bodyFatSaved({
        value: new Intl.NumberFormat(getLocale(), {
          maximumFractionDigits: 1,
        }).format(value),
      }),
    step: 0.5,
    tip: bodyFatTip,
    title: bodyFatTitle,
  },
  weight: {
    fallback: 75,
    fractionDigits: 1,
    label: weightLabel,
    max: 250,
    min: 30,
    // Stored in kilograms; the sentence reads in the person's unit (`D266`).
    saved: (value) =>
      weightSaved({ valueText: loadText(value, { maxFractionDigits: 1 }) }),
    step: 0.1,
    tip: weightTip,
    title: weightQuestion,
  },
};

/** 0.1 — the weigh-in's step, so a converted value lands on the dial's grid. */
function oneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

export function MeasurementEditor({
  host,
  kind,
}: {
  readonly host: EditorHost;
  readonly kind: MeasurementEditorId;
}) {
  const spec = SPECS[kind];
  const api = useApi();
  const locale = getLocale();
  const read = useSectionRead(
    measurementsOptions(api, { kind: MEASUREMENT_KINDS[kind].kind })
  );
  // Not crypto.randomUUID(): it does not exist on a plain-http origin (the
  // live-reload shell), where it crashed the editor.
  const [attemptKey] = useState(() => `${kind}-${uuidv7(Date.now())}`);
  const flow = useSaveFlow(useRecordMeasurementMutation(), host);
  const undo = useDeleteMeasurementMutation();
  const number = new Intl.NumberFormat(locale, {
    maximumFractionDigits: spec.fractionDigits,
  });
  const latest = read.data?.latest?.value ?? null;

  // What the card showed before this weigh-in: the undo puts it back while
  // the outbox carries the `DELETE` (`D194`).
  const before = read.data?.latest ?? null;
  const form = useEditorForm({
    onSubmit: (payload) =>
      flow.save(payload, (recorded) => ({
        message: spec.saved(recorded.value),
        undo: () => {
          undo({
            idempotencyKey: recorded.idempotencyKey,
            previousLatest: before,
          }).catch((error: unknown) => {
            console.error(
              "[profile] the weigh-in's undo was not queued",
              error
            );
          });
        },
      })),
    schema: measurementSchema,
    server: latest,
    toDraft: (value: number | null) => value ?? spec.fallback,
    toPayload: (value: number) => measurementInput(kind, value, attemptKey),
  });

  const { setDraft } = form;
  const setInUnit = useCallback(
    (value: number) => setDraft(fromUnit(value)),
    [setDraft]
  );

  return (
    <EditorShell
      eyebrow={eyebrowBodyToday({ date: formatDay(new Date(), locale) })}
      failure={
        flow.failure ??
        (Object.keys(form.errors).length > 0 ? errorField() : null)
      }
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      saveLabel={registerText()}
      title={spec.title()}
    >
      {kind === "weight" ? (
        // The weigh-in is typed in the person's unit and stored in
        // kilograms (`D266`): the dial holds the converted number, rounded
        // to the 0.1 it steps by, and every change goes back through
        // `fromUnit` — the payload's `unit` stays `kg`.
        <BigNumberForm
          format={number.format}
          label={spec.label()}
          max={maxInUnit(spec.max)}
          min={Math.floor(toUnit(spec.min))}
          onChange={setInUnit}
          previous={latest === null ? null : oneDecimal(toUnit(latest))}
          step={spec.step}
          tip={spec.tip()}
          unit={unitSymbol()}
          value={oneDecimal(toUnit(form.draft))}
        />
      ) : (
        <BigNumberForm
          format={number.format}
          label={spec.label()}
          max={spec.max}
          min={spec.min}
          onChange={form.setDraft}
          previous={latest}
          step={spec.step}
          tip={spec.tip()}
          unit={MEASUREMENT_KINDS[kind].unit}
          value={form.draft}
        />
      )}
    </EditorShell>
  );
}
